import * as React from 'react';
import React__default, { createContext, useState, useCallback, useContext, useEffect, useRef, Suspense } from 'react';

var defaultStartDelay = 300;
var defaultMinimalDuration = 1000;
var Context = createContext({
    count: 0,
    increment: function () {
        throw new Error('<SharedLoadingIndicatorContextProvider> is missing.');
    },
    decrement: function () {
        throw new Error('<SharedLoadingIndicatorContextProvider> is missing.');
    },
});
/**
 * Context provider to wrap your whole app in.
 *
 * ### Example
 *
 * ```jsx
 * import { SharedLoadingIndicatorContextProvider } from 'shared-loading-indicator'
 *
 * export const App => () => {
 * 	return (
 * 		<SharedLoadingIndicatorContextProvider>
 * 			My app
 * 		</SharedLoadingIndicatorContextProvider>
 * 	)
 * }
 * ```
 */
var SharedLoadingIndicatorContextProvider = function (_a) {
    var children = _a.children;
    var _b = useState(0), count = _b[0], setCount = _b[1];
    var increment = useCallback(function () {
        setCount(function (count) { return count + 1; });
    }, []);
    var decrement = useCallback(function () {
        setCount(function (count) { return count - 1; });
    }, []);
    return (React.createElement(Context.Provider, { value: { count: count, increment: increment, decrement: decrement } }, children));
};
/**
 * Hook `useLocalLoading` works similarly to `useState`. It returns array with `boolean` state indicating that component is loading and set function.
 *
 * ### Example
 *
 * ```jsx
 * import { useLocalLoading } from 'shared-loading-indicator'
 *
 * export const MyComponent => () => {
 * 	const [isLoading, setIsLoading] = useLocalLoading()
 *
 * 	return (
 * 		<div>
 * 			<div>
 * 				Is loading: {isLoading ? 'yes' : 'no'}
 * 			</div>
 * 			<button onClick={() => {
 * 				setIsLoading(!isLoading)
 * 			}}>
 * 		</div>
 * 	)
 * }
 * ```
 */
var useLocalLoading = function () {
    var _a = useState(false), isLoading = _a[0], setIsLoading = _a[1];
    var _b = useContext(Context), increment = _b.increment, decrement = _b.decrement;
    useEffect(function () {
        if (isLoading) {
            increment();
        }
        return function () {
            if (isLoading) {
                decrement();
            }
        };
    }, [isLoading]);
    return [isLoading, setIsLoading];
};
/**
 * Hook `useSharedLoading` returns `true` if some component is in loading state. Use this information to show your own loading indicator (spinner, progress bar, …).
 *
 * ### Example
 *
 * ```jsx
 * import { useSharedLoading } from 'shared-loading-indicator'
 *
 * export const LoadingIndicator => () => {
 * 	const isLoading = useSharedLoading()
 *
 * 	if (!isLoading) {
 * 		return null
 * 	}
 *
 * 	return (
 * 		<div>
 * 			App is loading something
 * 		</div>
 * 	)
 * }
 * ```
 */
var useSharedLoading = function (options) {
    if (options === void 0) { options = {}; }
    var _a = options.startDelay, startDelay = _a === void 0 ? defaultStartDelay : _a, _b = options.minimalDuration, minimalDuration = _b === void 0 ? defaultMinimalDuration : _b;
    var count = useContext(Context).count;
    var isLoading = count > 0;
    var _c = useState(isLoading), isThrottledLoading = _c[0], setIsThrottledLoading = _c[1];
    var loadingStart = useRef(new Date());
    useEffect(function () {
        if (isLoading) {
            loadingStart.current = new Date();
            var timer_1 = setTimeout(function () {
                setIsThrottledLoading(true);
            }, startDelay);
            return function () {
                clearTimeout(timer_1);
            };
        }
        else {
            var now = new Date();
            var durationAlreadyPassed = now.getTime() - loadingStart.current.getTime() - startDelay;
            var timer_2 = setTimeout(function () {
                setIsThrottledLoading(false);
            }, Math.max(0, minimalDuration - durationAlreadyPassed));
            return function () {
                clearTimeout(timer_2);
            };
        }
    }, [isLoading, minimalDuration, startDelay]);
    return isThrottledLoading;
};

/**
 * Mirrors first `isLoading` argument to `useLocalMirror` under the hood.
 *
 * ### Example
 *
 * ```jsx
 * import { useMirrorLoading } from 'shared-loading-indicator'
 * import { useQuery } from '@tanstack/react-query'
 *
 * const Mirror = () => {
 * 	const query = useQuery([], getData)
 * 	useMirrorLoading(query.isLoading)
 *
 * 	return <div>{query.data}</div>
 * }
 * ```
 */
var useMirrorLoading = function (isLoading) {
    var _a = useLocalLoading(); _a[0]; var setIsLoading = _a[1];
    useEffect(function () {
        setIsLoading(isLoading);
    }, [setIsLoading, isLoading]);
};

/**
 * Signalizes that something is loading.
 *
 * ### Example
 *
 * ```jsx
 * import { SharedLoadingIndicatorContextProvider, Loading } from 'shared-loading-indicator'
 *
 * export const App => () => {
 * 	const somethingIsLoading = true // Hook this to a state
 *
 * 	return (
 * 		<SharedLoadingIndicatorContextProvider>
 * 			{somethingIsLoading && <Loading/>}
 * 			My app
 * 		</SharedLoadingIndicatorContextProvider>
 * 	)
 * }
 * ```
 */
var Loading = function (_a) {
    var _b = _a.isLoading, isLoading = _b === void 0 ? true : _b;
    useMirrorLoading(isLoading);
    return null;
};

/**
 * Suspense wrapper for lazy loaded components.
 *
 * ### Example
 *
 * ```jsx
 * import { LoadingSuspense } from 'shared-loading-indicator'
 *
 * const LazyWithSuspense = () => {
 * 	return (
 * 		<LoadingSuspense>
 * 			<LazyComponent />
 * 		</LoadingSuspense>
 * 	)
 * }
 * ```
 */
var LoadingSuspense = function (_a) {
    var children = _a.children, fallback = _a.fallback;
    return (React__default.createElement(Suspense, { fallback: React__default.createElement(React__default.Fragment, null,
            React__default.createElement(Loading, null),
            fallback) }, children));
};

/**
 * Styled visual indicator of loading.
 */
var ProgressLoadingIndicator = function () {
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement("style", { dangerouslySetInnerHTML: {
                __html: "\n\t\t\t\t\t\t.ProgressLoadingIndicator {\n\t\t\t\t\t\t\tposition: relative;\n\t\t\t\t\t\t\theight: 0.3125em;\n\t\t\t\t\t\t\tbackground-color: var(--ProgressLoadingIndicator-color, #027aff);\n\t\t\t\t\t\t\toverflow: hidden;\n\t\t\t\t\t\t}\n\t\t\t\t\t\t.ProgressLoadingIndicator::before {\n\t\t\t\t\t\t\tcontent: '';\n\t\t\t\t\t\t\tposition: absolute;\n\t\t\t\t\t\t\tinset: 0;\n\t\t\t\t\t\t\tbackground-color: var(--ProgressLoadingIndicator-other-color, rgba(255, 255, 255, 0.4));\n\t\t\t\t\t\t\tanimation: ProgressLoadingIndicator-wipe 1s infinite ease-in-out;\n\t\t\t\t\t\t}\n\t\t\t\t\t\t@keyframes ProgressLoadingIndicator-wipe {\n\t\t\t\t\t\t\t0% {\n\t\t\t\t\t\t\t\ttransform: translateX(-100%);\n\t\t\t\t\t\t\t}\n\t\t\t\t\t\t\t100% {\n\t\t\t\t\t\t\t\ttransform: translateX(100%) scaleX(0.3);\n\t\t\t\t\t\t\t}\n\t\t\t\t\t\t}\n\t\t\t\t\t\t@media (prefers-reduced-motion) {\n\t\t\t\t\t\t\t.ProgressLoadingIndicator::before {\n\t\t\t\t\t\t\t\tanimation-duration: 20s;\n\t\t\t\t\t\t\t}\n\t\t\t\t\t\t}\n\t\t\t\t\t",
            } }),
        React__default.createElement("div", { className: "ProgressLoadingIndicator" })));
};

/**
 * Place `SharedProgressLoadingIndicator` inside `SharedLoadingIndicatorContextProvider` to use prestyled loading indicator. See [demo](https://shared-loading-indicator.netlify.app) to change color or placement.
 *
 * ### Example
 *
 * ```jsx
 * import { SharedLoadingIndicatorContextProvider, SharedProgressLoadingIndicator } from 'shared-loading-indicator'
 *
 * export const App => () => {
 * 	return (
 * 		<SharedLoadingIndicatorContextProvider>
 * 			<SharedProgressLoadingIndicator/>
 * 			My app
 * 		</SharedLoadingIndicatorContextProvider>
 * 	)
 * }
 * ```
 */
var SharedProgressLoadingIndicator = function (_a) {
    var _b = _a.placement, placement = _b === void 0 ? 'top' : _b, startDelay = _a.startDelay, minimalDuration = _a.minimalDuration;
    var isLoading = useSharedLoading({ startDelay: startDelay, minimalDuration: minimalDuration });
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement("style", { dangerouslySetInnerHTML: {
                __html: "\n\t\t\t\t\t\t.SharedProgressLoadingIndicator {\n\t\t\t\t\t\t\tposition: fixed;\n\t\t\t\t\t\t\tleft: 0;\n\t\t\t\t\t\t\ttop: 0;\n\t\t\t\t\t\t\tright: 0;\n\t\t\t\t\t\t\tz-index: 10;\n\t\t\t\t\t\t}\n\t\t\t\t\t\t.SharedProgressLoadingIndicator__in {\n\t\t\t\t\t\t\tposition: absolute;\n\t\t\t\t\t\t\ttop: 0;\n\t\t\t\t\t\t\tleft: 0;\n\t\t\t\t\t\t\tright: 0;\n\t\t\t\t\t\t\ttransform: translateY(-100%);\n\t\t\t\t\t\t\ttransition-property: visibility, transform;\n\t\t\t\t\t\t\ttransition-duration: 0.2s;\n\t\t\t\t\t\t\tvisibility: hidden;\n\t\t\t\t\t\t}\n\t\t\t\t\t\t.SharedProgressLoadingIndicator--placement-bottom,\n\t\t\t\t\t\t.SharedProgressLoadingIndicator--placement-bottom .SharedProgressLoadingIndicator__in {\n\t\t\t\t\t\t\ttop: auto;\n\t\t\t\t\t\t\tbottom: 0;\n\t\t\t\t\t\t}\n\t\t\t\t\t\t.SharedProgressLoadingIndicator--placement-bottom .SharedProgressLoadingIndicator__in {\n\t\t\t\t\t\t\ttransform: translateY(100%);\n\t\t\t\t\t\t}\n\t\t\t\t\t\t.SharedProgressLoadingIndicator--loading .SharedProgressLoadingIndicator__in {\n\t\t\t\t\t\t\ttransform: none;\n\t\t\t\t\t\t\tvisibility: inherit;\n\t\t\t\t\t\t}\n\t\t\t\t\t\t@media (prefers-reduced-motion) {\n\t\t\t\t\t\t\t.SharedProgressLoadingIndicator__in {\n\t\t\t\t\t\t\t\ttransition-duration: 0s;\n\t\t\t\t\t\t\t}\n\t\t\t\t\t\t}\n\t\t\t\t\t",
            } }),
        React__default.createElement("div", { className: "SharedProgressLoadingIndicator SharedProgressLoadingIndicator--placement-".concat(placement).concat(isLoading ? ' SharedProgressLoadingIndicator--loading' : '') },
            React__default.createElement("div", { className: "SharedProgressLoadingIndicator__in" },
                React__default.createElement(ProgressLoadingIndicator, null)))));
};

export { Loading, LoadingSuspense, ProgressLoadingIndicator, SharedLoadingIndicatorContextProvider, SharedProgressLoadingIndicator, useLocalLoading, useMirrorLoading, useSharedLoading };
//# sourceMappingURL=index.es.js.map
